import {
  Datagrid,
  TextField,
  SelectField,
  ReferenceField,
  ReferenceArrayField,
  SingleFieldList,
  useListContext,
  FunctionField,
  ArrayField,
  Button,
  useRedirect,
  useRecordContext,
  SimpleList,
} from "react-admin";
import { administrationRequests } from "api/administrationApi";
import { NationalityType, WeekDaysChoices, WeekTypeChoices } from "utils/choices";
import { Box, Chip, useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import {
  DISCIPLINES,
  FOR_UPDATE,
  MOBILE_BREAKPOINT,
  StudentServicePath,
  WEEKLY_SCHEDULE,
} from "utils/constants";
import { useLocation } from "react-router-dom";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import RoomField from "server/components/Fields/RoomField";
import GoogleRoomField from "server/components/Fields/GoogleRoomField";
import { employeeRequests } from "api/employeeApi";
import { studentRequests } from "api/studentApi";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";

// A schedule is held either in a physical room or in a Google classroom
const ScheduleRoomField = () => {
  const record = useRecordContext();

  return record?.isOnline ? (
    <GoogleRoomField />
  ) : (
    <RoomField
      source="roomId"
      render={rec => `${rec?.roomName} - ${rec?.buildingName} (${rec?.capacityExam})`}
    />
  );
};

const WeeklyScheduleDisciplineTable = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const weeklyScheduleId = params.get("weeklyScheduleId");

  const { data, isLoading } = useListContext();
  const redirect = useRedirect();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  if (isLoading || !data || data.length === 0) return null;

  const professorsButton = record => (
    <SecuredComponent
      permission={
        permissions.canUpdateWeeklyScheduleProfessors || permissions.canUpdateEverythingInWeeklySchedules
      }
    >
      <Button
        label="Преподавател"
        startIcon={record.professorsIds.length < 1 ? <AddIcon /> : <EditIcon />}
        onClick={() =>
          redirect(
            `/${StudentServicePath}${WEEKLY_SCHEDULE}/${weeklyScheduleId}/${DISCIPLINES}/${record.disciplineId}/${FOR_UPDATE}?id=${record.id}&specialityId=${record.specialityId}&isEditProfessors=true`,
          )
        }
      />
    </SecuredComponent>
  );

  const roomButton = record => (
    <SecuredComponent
      permission={
        permissions.canUpdateWeeklySchedulesRoom ||
        permissions.canUpdateEverythingInWeeklySchedules ||
        permissions.canSeeClassroomsScheduleTab
      }
    >
      <Button
        label="Зала"
        startIcon={!record.roomId && !record.googleCourseRefId ? <AddIcon /> : <EditIcon />}
        onClick={() =>
          redirect(
            `/${StudentServicePath}${WEEKLY_SCHEDULE}/${weeklyScheduleId}/${DISCIPLINES}/${record.disciplineId}/${FOR_UPDATE}?id=${record.id}&specialityId=${record.specialityId}&isEditRooms=true`,
          )
        }
      />
    </SecuredComponent>
  );

  if (isMobile) {
    return (
      <SimpleList
        linkType={false}
        sx={settings.style.mobileList}
        primaryText={record => (
          <div>
            <MobileListRow
              fields={[
                {
                  label: "Дисциплина",
                  value: (
                    <ReferenceField
                      source="disciplineId"
                      reference={administrationRequests.getDisciplinesSelect()}
                    >
                      <TextField source="name" />
                    </ReferenceField>
                  ),
                },
              ]}
            />
            <MobileListRow
              fields={[
                { label: "Ден", value: WeekDaysChoices.find(d => d.id === record.weekDay)?.name },
                {
                  label: "Час",
                  value: `${record.startTime.slice(0, 5)} - ${record.endTime.slice(0, 5)}`,
                },
              ]}
            />
          </div>
        )}
        secondaryText={record => (
          <div>
            <MobileListRow
              fields={[
                {
                  label: "Специалност",
                  value: (
                    <ReferenceField
                      source="specialityId"
                      reference={administrationRequests.getSpecialitiesSelect()}
                    >
                      <TextField source="name" />
                    </ReferenceField>
                  ),
                },
              ]}
            />
            <MobileListRow
              fields={[
                {
                  label: "Учебен план",
                  value: (
                    <ReferenceField
                      source="curriculumId"
                      reference={studentRequests.getCurriculumsSelect()}
                    >
                      <TextField source="name" />
                    </ReferenceField>
                  ),
                },
              ]}
            />
            <MobileListRow
              fields={[
                {
                  label: "Модул",
                  value: (
                    <TextField
                      source="curriculumModuleName"
                      emptyText="-"
                    />
                  ),
                },
              ]}
            />
            {record.nationality && (
              <MobileListRow
                fields={[
                  {
                    label: "Националност",
                    value: NationalityType.find(n => n.id === record.nationality)?.name,
                  },
                ]}
              />
            )}
            <MobileListRow
              fields={[
                {
                  label: "Тип",
                  value: (
                    <ReferenceField
                      source="classTypeId"
                      reference={administrationRequests.getRoomClassTypesSelect()}
                    >
                      <TextField source="name" />
                    </ReferenceField>
                  ),
                },
              ]}
            />
            <MobileListRow
              fields={[
                { label: "Тип седмица", value: WeekTypeChoices.find(d => d.id === record.weekType)?.name },
              ]}
            />
            <MobileListRow
              fields={[
                {
                  label: "Курс",
                  value:
                    record.course === 1
                      ? "1-ви Курс"
                      : record.course === 2
                        ? "2-ри Курс"
                        : record.course === 3
                          ? "3-ти Курс"
                          : record.course === 4
                            ? "4-ти Курс"
                            : record.course === 5
                              ? "5-ти Курс"
                              : "6-ти Курс",
                },
              ]}
            />
            <MobileListRow
              fields={[
                {
                  label: record.isOnline ? "Google зала" : "Зала",
                  value: <ScheduleRoomField />,
                },
              ]}
            />
            <MobileListRow
              fields={[
                {
                  label: "Групи",
                  value: (
                    <ArrayField source="studyGroups">
                      <SingleFieldList linkType={false}>
                        <FunctionField
                          render={rec => (
                            <Chip
                              key={rec}
                              label={rec}
                            />
                          )}
                        />
                      </SingleFieldList>
                    </ArrayField>
                  ),
                },
              ]}
            />
            <MobileListRow
              fields={[
                {
                  label: "Преподаватели",
                  value: (
                    <ReferenceArrayField
                      source="professorsIds"
                      reference={employeeRequests.getProfessorsSelect()}
                    >
                      <SingleFieldList linkType={false}>
                        <FunctionField
                          render={rec => {
                            const scientificDegree = rec.scientificDegreeNameBg ?? "";
                            return rec.secondName
                              ? `${scientificDegree} ${rec.firstName} ${rec.secondName} ${rec.lastName}`
                              : `${scientificDegree} ${rec.firstName} ${rec.lastName}`;
                          }}
                        />
                      </SingleFieldList>
                    </ReferenceArrayField>
                  ),
                },
              ]}
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              <Box sx={{ display: "flex", gap: 3 }}>
                {professorsButton(record)}
                {roomButton(record)}
              </Box>
            </div>
          </div>
        )}
      />
    );
  }

  return (
    <Datagrid
      rowClick={false}
      bulkActionButtons={false}
    >
      <FunctionField
        label="Ден"
        render={record => WeekDaysChoices.find(d => d.id === record.weekDay)?.name}
      />
      <FunctionField
        label="Час"
        render={record => `${record.startTime.slice(0, 5)} - ${record.endTime.slice(0, 5)}`}
      />
      <ReferenceField
        source="specialityId"
        reference={administrationRequests.getSpecialitiesSelect()}
        label="Специалност"
      >
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField
        source="curriculumId"
        reference={studentRequests.getCurriculumsSelect()}
        label="Учебен план"
      >
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField
        source="disciplineId"
        reference={administrationRequests.getDisciplinesSelect()}
        label="Дисциплина"
      >
        <TextField source="name" />
      </ReferenceField>
      <TextField
        source="curriculumModuleName"
        label="Модул"
        sortable={false}
        emptyText="-"
      />
      <SelectField
        source="nationality"
        choices={NationalityType}
        label="Националност"
        sortable={false}
        emptyText="-"
      />
      <ReferenceField
        source="classTypeId"
        reference={administrationRequests.getRoomClassTypesSelect()}
        label="Тип"
      >
        <TextField source="name" />
      </ReferenceField>
      <FunctionField
        label="Тип седмица"
        render={record => WeekTypeChoices.find(d => d.id === record.weekType)?.name}
      />
      <FunctionField
        label="Курс"
        render={record =>
          record.course === 1
            ? "1-ви Курс"
            : record.course === 2
              ? "2-ри Курс"
              : record.course === 3
                ? "3-ти Курс"
                : record.course === 4
                  ? "4-ти Курс"
                  : record.course === 5
                    ? "5-ти Курс"
                    : "6-ти Курс"
        }
      />

      <ScheduleRoomField
        label="Зала"
        sortable={false}
      />
      <ArrayField
        source="studyGroups"
        label="Групи"
      >
        <SingleFieldList linkType={false}>
          <FunctionField
            render={rec => (
              <Chip
                key={rec}
                label={rec}
              />
            )}
          />
        </SingleFieldList>
      </ArrayField>
      <ReferenceArrayField
        source="professorsIds"
        reference={employeeRequests.getProfessorsSelect()}
        label="Преподаватели"
      >
        <SingleFieldList linkType={false}>
          <FunctionField
            render={rec => {
              const scientificDegree = rec.scientificDegreeNameBg ?? "";
              return rec.secondName
                ? `${scientificDegree} ${rec.firstName} ${rec.secondName} ${rec.lastName}`
                : `${scientificDegree} ${rec.firstName} ${rec.lastName}`;
            }}
          />
        </SingleFieldList>
      </ReferenceArrayField>
      <FunctionField
        render={record => (
          <Box sx={{ display: "flex", gap: 3 }}>
            {professorsButton(record)}
            {roomButton(record)}
          </Box>
        )}
      />
    </Datagrid>
  );
};

export default WeeklyScheduleDisciplineTable;
